'use client'

import { useState } from 'react'
import { useRedeemVoucherMutation } from '@/api/user/organization/credits/redeem-voucher-mutation'
import { useGetCreditHistoryQuery } from '@/api/user/organization/credits/get-credit-history-query'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { useSettingsModal } from '../../settings-modal-context'
import { CreditPoolHistoryList } from '../../credit-pool-card'
import { SettingsSection } from '../../settings-section'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/features/toast/toast'
import { Ticket, Sparkles } from 'lucide-react'

const ITEMS_PER_PAGE = 10

export const VouchersTab = () => {
    const organizationId = useOrganizationId()
    const { setOptions } = useSettingsModal()
    const [code, setCode] = useState('')
    const [redeemed, setRedeemed] = useState<{ credits: number } | null>(null)
    const [page, setPage] = useState(1)

    const redeemMutation = useRedeemVoucherMutation()

    const { data: historyData, isLoading: isLoadingHistory } = useGetCreditHistoryQuery(organizationId, {
        page,
        limit: ITEMS_PER_PAGE,
        type: 'PROMOTIONAL',
    })

    const creditPools = historyData?.data?.creditPools ?? []
    const meta = historyData?.meta
    const totalPages = meta ? Math.ceil(meta.totalCount / meta.limit) : 1

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const trimmed = code.trim()
        if (!trimmed) {
            toast.error('Please enter a voucher code')
            return
        }

        try {
            const result = await redeemMutation.mutateAsync({ code: trimmed })
            const credits = result?.creditPool?.totalCredits
            if (credits) {
                setRedeemed({ credits })
                setCode('')
                toast.success(`${credits.toLocaleString()} credits added to your account`)
            } else {
                toast.success('Voucher redeemed successfully')
            }
        } catch (error: unknown) {
            const apiError = error as Record<string, unknown> | undefined
            const errors = apiError?.errors as Array<{ message?: string }> | undefined
            const message = errors?.[0]?.message ?? 'Failed to redeem voucher code'

            const SUBSCRIPTION_REQUIRED_MESSAGE = 'An active subscription is required to redeem this voucher code'

            if (message === SUBSCRIPTION_REQUIRED_MESSAGE) {
                toast.error(
                    <span>
                        An active subscription is required to redeem this voucher code.{' '}
                        <button
                            type="button"
                            className="underline font-medium"
                            onClick={() => {
                                setOptions({ activeTab: 'plans', plansSource: 'vouchers_tab' })
                                toast.dismiss()
                            }}
                        >
                            Go to Plans
                        </button>
                    </span>
                )
            } else {
                toast.error(message)
            }
        }
    }

    return (
        <div className="flex flex-col gap-8">
            <SettingsSection
                title="Redeem Credits Voucher"
                description='Enter a credits voucher code to receive credits for your workspace. Please note: This is only for credits vouchers. If you have received a promo code for your subscription, navigate to "Plans" and enter it in the checkout flow.'
            >
                {redeemed ? (
                    <div className="flex items-center gap-4 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 shrink-0">
                            <Sparkles className="h-5 w-5 text-violet-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-[14px] font-medium text-[#171717]">Credits Voucher Redeemed!</p>
                            <p className="text-[13px] text-[#6B7280]">
                                <span className="font-semibold text-[#171717]">
                                    {redeemed.credits.toLocaleString()}
                                </span>{' '}
                                credits have been added to your account.
                            </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setRedeemed(null)}>
                            Redeem Another
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex items-end gap-3">
                        <div className="flex-1 max-w-sm">
                            <div className="relative">
                                <Ticket className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="Enter voucher code"
                                    className="pl-9 font-mono tracking-wider uppercase"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={!code.trim()}
                            isLoading={redeemMutation.isPending}
                            className="gap-2"
                        >
                            <Ticket className="w-4 h-4" />
                            Redeem
                        </Button>
                    </form>
                )}
            </SettingsSection>

            <SettingsSection title="Credits Voucher History">
                <CreditPoolHistoryList
                    pools={creditPools}
                    isLoading={isLoadingHistory}
                    emptyIcon={Ticket}
                    emptyMessage="No redeemed credits vouchers yet."
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                />
            </SettingsSection>
        </div>
    )
}
